import {useCallback, useMemo} from 'react';

import {TableModelContext} from 'notebook/tables/store/contexts/TableModelContext';
import {
  computeCopySelectionHtml,
  computeCopySelectionText,
} from 'notebook/tables/store/logic/TableClipboardLogic';

export interface TableClipboardGetters {
  /**
   * Serializes the current selection as tab/newline separated plain text. Returns an empty string
   * when there is nothing to copy.
   */
  getCopySelectionText: () => string;

  /**
   * Serializes the current selection as an HTML table, which preserves formulas and formatting when
   * pasted back into a Benchling table. Returns an empty string when there is nothing to copy.
   */
  getCopySelectionHtml: () => string;

  /**
   * Whether the current selection contains a cell locked on the table itself, which is what
   * disallows cutting it.
   */
  getHasLockedCellInSelection: () => boolean;
}

/**
 * Hook providing what a consumer needs to cut or copy the current selection.
 *
 * Every value is read fresh from the store when its getter is called rather than subscribed to, so
 * a consumer that only needs them on demand (such as when the cell context menu opens) does not
 * re-render as the selection moves.
 */
export function useTableClipboard(): TableClipboardGetters {
  const {useTableEditableStore, tableModelSelector} = TableModelContext.useContext();

  const getTableModel = useCallback(
    () => tableModelSelector(useTableEditableStore.getState()),
    [useTableEditableStore, tableModelSelector]
  );

  return useMemo<TableClipboardGetters>(
    () => ({
      getCopySelectionText: () => {
        const {selection, props} = getTableModel();
        return computeCopySelectionText({
          hasRealSelection: selection.hasRealSelection,
          expandedBox: selection.expandedBox,
          table: props.table,
          computedTable: props.computedTable,
          showFormulas: false,
        });
      },
      getCopySelectionHtml: () => {
        const {selection, props} = getTableModel();
        return computeCopySelectionHtml({
          hasRealSelection: selection.hasRealSelection,
          expandedBox: selection.expandedBox,
          table: props.table,
          computedTable: props.computedTable,
          disableFormulas: props.disableFormulas ?? false,
          showFormulas: false,
        });
      },
      getHasLockedCellInSelection: () => getTableModel().locking.hasLockedCellInSelection,
    }),
    [getTableModel]
  );
}
